import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Toaster } from 'sonner';

import { ErreurApi } from '@/shared/api/erreurs';

/**
 * Client de requetes.
 *
 * `retry` ne rejoue QUE le transitoire. Sur cet outil, rejouer un 403 reviendrait a
 * marteler l'API de tentatives refusees — et a remplir les journaux de securite du
 * serveur d'un bruit qui ressemble a une attaque.
 *
 * `staleTime` court (10 s) : un back-office affiche des etats qui bougent — suspensions,
 * paiements en echec — et montrer une donnee d'il y a cinq minutes comme si elle etait
 * fraiche conduit a agir sur une situation qui n'existe plus.
 */
function creerClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 10_000,
        gcTime: 2 * 60_000,
        refetchOnWindowFocus: true,
        retry(nombreEchecs, erreur) {
          if (erreur instanceof ErreurApi && !erreur.estTransitoire) return false;
          return nombreEchecs < 2;
        },
      },
      mutations: {
        // Une mutation d'administration ne se rejoue jamais toute seule : elle peut avoir
        // abouti malgre une reponse perdue, et la rejouer suspendrait deux fois.
        retry: false,
      },
    },
  });
}

export function Providers({ children }: { children: ReactNode }) {
  const [client] = useState(creerClient);

  return (
    <QueryClientProvider client={client}>
      {children}

      <Toaster
        position="bottom-right"
        theme="dark"
        richColors
        closeButton
        toastOptions={{
          classNames: {
            toast: 'bg-fond-eleve border-bordure text-texte rounded-[--radius-astro]',
            description: 'text-texte-doux',
          },
        }}
      />
    </QueryClientProvider>
  );
}
